import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';
import AudioRecorder from '../components/AudioRecorder';
import ImageViewer from '../components/ImageViewer';
import RoslinBranding from '../components/RoslinBranding';

interface Option {
  id: number;
  text: string;
  is_correct: boolean;
}

interface Question {
  id: number;
  text: string;
  question_type: 'multiple_choice' | 'text' | 'audio_response';
  audio_file?: string;
  image_file?: string;
  images?: Array<{ image_file: string; caption?: string; order_num?: number }>;
  order_num: number;
  options?: Option[];
  allow_multiple_answers?: boolean;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  category?: string;
  quiz_type?: 'quiz' | 'evaluation' | 'assessment' | 'survey';
  time_limit_minutes?: number; // Time limit in minutes
  require_email?: boolean; // Whether email is required for this quiz
  audio_max_plays?: number | null;
  audio_allow_pause?: boolean;
  audio_allow_seek?: boolean;
  questions: Question[];
  audioGroups?: Array<{
    audio_file: string;
    questions: Question[];
  }>;
  questionsWithoutAudio?: Question[];
  imageGroups?: Array<{ image_file: string; questions: Question[] }>;
  questionsWithoutImage?: Question[];
}

interface UserResponse {
  question_id: number;
  option_id?: number; // For backward compatibility with single selection
  selected_option_ids?: number[]; // For multiple selections
  text_answer?: string;
  audio_answer?: Blob;
  audio_file?: string;
}

interface SavedProgress {
  quizId?: string;
  quiz_id?: number;
  responses: UserResponse[];
  currentQuestionIndex?: number;
  current_question_index?: number;
  userName?: string;
  participant_name?: string;
  userEmail?: string;
  participant_email?: string;
  startTime?: string;
  started_at?: string;
  lastSaved?: string;
  last_saved_at?: string;
  elapsedSeconds?: number;
  elapsed_seconds?: number;
}

const TakeQuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<UserResponse[]>([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAnswerCount, setSubmittedAnswerCount] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [showUserDetailsForm, setShowUserDetailsForm] = useState(true);
  
  // Auto-save states
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [serverProgressId, setServerProgressId] = useState<number | null>(null);
  const [serverSaveFailed, setServerSaveFailed] = useState(false);
  const [serverSaveError, setServerSaveError] = useState<string | null>(null);
  const lastSaveAttemptRef = useRef<Date | null>(null); // Track last save attempt to prevent spam
  const elapsedSecondsRef = useRef(0); // Track elapsed seconds without triggering re-renders
  const lastSavedResponsesRef = useRef<string>(''); // Track last saved responses to prevent duplicate saves
  const [sessionId] = useState<string>(() => {
    // Generate a unique session ID for this quiz attempt
    return `quiz_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  // Timer states
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null); // in seconds
  const [timerExpired, setTimerExpired] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0); // Track actual quiz time

  // Debug effect to track state changes
  useEffect(() => {
    console.log('State changed:', { 
      submitted, 
      submitting, 
      showReview, 
      showUserDetailsForm, 
      timerExpired,
      startTime: startTime?.toISOString(),
      userName,
      userEmail
    });
  }, [submitted, submitting, showReview, showUserDetailsForm, timerExpired, startTime, userName, userEmail]);

  // Generate storage key for this quiz attempt
  const getStorageKey = () => `quiz_progress_${id}`;

  // Plays used per audio file. Kept in localStorage (and not cleared on "start fresh")
  // so reloading or restarting doesn't reset the admin's play limit.
  const audioPlaysKey = `quiz_audio_plays_${id}`;
  const [audioPlayCounts, setAudioPlayCounts] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem(audioPlaysKey) || '{}');
    } catch {
      return {};
    }
  });

  const recordAudioPlay = useCallback((audioFile: string) => {
    setAudioPlayCounts(prev => {
      const next = { ...prev, [audioFile]: (prev[audioFile] || 0) + 1 };
      localStorage.setItem(audioPlaysKey, JSON.stringify(next));
      return next;
    });
  }, [audioPlaysKey]);

  const clearAudioPlayCounts = useCallback(() => {
    localStorage.removeItem(audioPlaysKey);
    setAudioPlayCounts({});
  }, [audioPlaysKey]);

  const audioRestrictionProps = (audioFile: string) => ({
    maxPlays: quiz?.audio_max_plays || null,
    playsUsed: audioPlayCounts[audioFile] || 0,
    onPlayStart: () => recordAudioPlay(audioFile),
    allowPause: quiz?.audio_allow_pause !== false,
    allowSeek: quiz?.audio_allow_seek !== false
  });

  // Save progress to localStorage (fallback/backup)
  // This can be called synchronously (e.g., on beforeunload)
  const saveToLocalStorage = useCallback((data: Partial<SavedProgress>, clearUnsavedFlag: boolean = true) => {
    if (!id) return;
    
    try {
      const existingData = localStorage.getItem(getStorageKey());
      const currentData = existingData ? JSON.parse(existingData) : {};
      
      const updatedData: SavedProgress = {
        quizId: id,
        responses: responses,
        currentQuestionIndex,
        userName,
        userEmail,
        startTime: startTime?.toISOString() || new Date().toISOString(),
        lastSaved: new Date().toISOString(),
        elapsedSeconds: elapsedSeconds,
        ...currentData,
        ...data
      };
      
      localStorage.setItem(getStorageKey(), JSON.stringify(updatedData));
      // Clear unsaved changes flag when localStorage save succeeds (if requested)
      if (clearUnsavedFlag) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      }
      console.log('Progress saved to localStorage');
      return true;
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      return false;
    }
  }, [id, responses, currentQuestionIndex, userName, userEmail, startTime, elapsedSeconds]);

  // Save progress to server
  const saveToServer = useCallback(async () => {
    if (!id || !userName.trim() || !startTime) {
      console.warn('Cannot save to server - missing required data:', { 
        id, 
        userName: userName.trim(), 
        startTime: !!startTime 
      });
      return;
    }
    
    try {
      setSaving(true);
      
      const progressData = {
        participant_name: userName,
        participant_email: userEmail || '',
        session_id: sessionId,
        started_at: startTime.toISOString(),
        current_question_index: currentQuestionIndex,
        elapsed_seconds: elapsedSecondsRef.current, // Use ref to avoid dependency on timer ticks
        responses: responses
      };
      
      console.log('Attempting to save progress to server:', {
        quizId: id,
        participantName: userName,
        responsesCount: responses.length,
        currentQuestionIndex,
        hasResponses: responses.some(r => r.option_id || r.selected_option_ids || r.text_answer || r.audio_file)
      });
      
      const response = await api.post(`/api/quizzes/${id}/progress`, progressData);
      
      if (response.data.progress_id) {
        setServerProgressId(response.data.progress_id);
        console.log('✅ Progress saved to server successfully. Progress ID:', response.data.progress_id);
      } else {
        console.warn('⚠️ Server save succeeded but no progress_id returned:', response.data);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      // Clear error state on successful save
      setServerSaveFailed(false);
      setServerSaveError(null);
      // Update saved responses hash to prevent duplicate saves
      lastSavedResponsesRef.current = JSON.stringify(responses);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const errorStatus = error.response?.status;
      
      console.error('❌ Failed to save progress to server:', {
        error: error.message,
        status: errorStatus,
        statusText: error.response?.statusText,
        data: error.response?.data,
        quizId: id,
        participantName: userName
      });
      
      // Set error state to show warning to user
      setServerSaveFailed(true);
      setServerSaveError(errorStatus === 401 || errorStatus === 403 
        ? 'Authentication failed. Progress saved locally only.'
        : errorStatus === 500
        ? 'Server error. Progress saved locally only.'
        : errorStatus === 404
        ? 'Quiz not found. Progress saved locally only.'
        : `Failed to save to server (${errorStatus || 'Network error'}). Progress saved locally only.`);
      
      // Fallback to localStorage if server save fails (good backup)
      // This ensures progress isn't lost even if server is down
      saveToLocalStorage({});
    } finally {
      setSaving(false);
    }
  }, [id, userName, userEmail, sessionId, startTime, currentQuestionIndex, responses, saveToLocalStorage]); // Removed elapsedSeconds to prevent timer spam

  // Load progress from server
  const loadFromServer = useCallback(async () => {
    if (!id) return null;
    
    try {
      const response = await api.get(`/api/quizzes/${id}/progress`, {
        params: { session_id: sessionId }
      });
      
      if (response.data.has_progress && response.data.progress) {
        console.log('Loaded progress from server:', response.data.progress);
        return response.data.progress;
      }
    } catch (error: any) {
      // 404 is expected when there's no saved progress
      if (error.response?.status !== 404) {
        console.error('Failed to load progress from server:', error);
      }
    }
    return null;
  }, [id, sessionId]);

  // Load progress from localStorage (fallback)
  const loadFromLocalStorage = useCallback(() => {
    if (!id) return null;
    
    try {
      const savedData = localStorage.getItem(getStorageKey());
      if (savedData) {
        const parsed: SavedProgress = JSON.parse(savedData);
        if (parsed.quizId === id) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
    return null;
  }, [id]);

  // Clear saved progress (both server and local)
  const clearSavedProgress = useCallback(async () => {
    if (!id) return;
    
    // Clear from server
    try {
      await api.delete(`/api/quizzes/${id}/progress`, {
        params: { session_id: sessionId }
      });
      console.log('Cleared server progress for quiz', id);
    } catch (error) {
      console.error('Failed to clear server progress:', error);
    }
    
    // Clear from localStorage
    localStorage.removeItem(getStorageKey());
    setLastSaved(null);
    setHasUnsavedChanges(false);
    setServerProgressId(null);
    // Clear error state when clearing progress
    setServerSaveFailed(false);
    setServerSaveError(null);
    console.log('Cleared saved progress for quiz', id);
  }, [id, sessionId]);

  // Clear all localStorage for this quiz (for debugging)
  const clearAllProgress = useCallback(() => {
    if (!id) return;
    localStorage.removeItem(getStorageKey());
    console.log('Cleared ALL progress for quiz', id);
  }, [id]);

  // Auto-save effect (save to server with smart debouncing to prevent spam)
  // This is critical - saves to server database for logging
  useEffect(() => {
    if (!quiz || submitted || !startTime || !userName.trim()) return;
    
    // Check if there are actual responses to save (not just empty array)
    const hasActualResponses = responses.some(r => 
      r.option_id !== undefined || 
      (r.selected_option_ids && r.selected_option_ids.length > 0) || 
      r.text_answer?.trim() || 
      r.audio_file
    );
    
    if (!hasActualResponses && !hasUnsavedChanges) return; // Skip if no changes and no responses

    // Create a hash of responses to detect actual changes
    // Only skip if hash matches AND there are no unsaved changes
    // If hasUnsavedChanges is true, we MUST save (user just made a change)
    const responsesHash = JSON.stringify(responses);
    
    // Skip if responses haven't actually changed AND there are no unsaved changes
    // This prevents timer-triggered saves, but allows saves when user makes changes
    // IMPORTANT: Always allow save if hasUnsavedChanges is true (user just made a change)
    if (responsesHash === lastSavedResponsesRef.current && !hasUnsavedChanges) {
      return; // No actual changes, don't save
    }
    
    // If we have unsaved changes, we MUST save regardless of hash
    // This ensures user actions (multiple choice, text, audio) always trigger saves

    // Determine debounce time based on response type
    // Text answers: longer debounce (1.5s) since user is typing continuously
    // Multiple choice/audio: shorter debounce (500ms) since it's a single action
    const hasTextAnswer = responses.some(r => r.text_answer?.trim());
    const debounceTime = hasTextAnswer ? 1500 : 500; // 1.5s for text, 500ms for others

    // Save to server with smart debouncing to prevent spam
    const autoSaveTimer = setTimeout(() => {
      // Double-check we still have unsaved changes (might have been saved by another trigger)
      if (!hasUnsavedChanges && !hasActualResponses) return;
      
      // Don't save if already saving
      if (saving) {
        console.log('Skipping save - already saving');
        return;
      }
      
      // Double-check we still have unsaved changes (might have been saved by another trigger)
      if (!hasUnsavedChanges && !hasActualResponses) {
        console.log('Skipping save - no unsaved changes and no responses');
        return;
      }
      
      // Double-check responses haven't changed since we queued this save
      const currentResponsesHash = JSON.stringify(responses);
      if (currentResponsesHash === lastSavedResponsesRef.current && !hasUnsavedChanges) {
        console.log('Skipping save - responses unchanged and no unsaved changes flag');
        return;
      }
      
      // Prevent spam: Don't save if we just saved recently (within last 1 second)
      // But ONLY if responses haven't actually changed (to allow legitimate saves)
      const now = new Date();
      const minTimeBetweenSaves = 1000; // 1 second minimum between saves
      if (lastSaveAttemptRef.current && currentResponsesHash === lastSavedResponsesRef.current) {
        const timeSinceLastSave = now.getTime() - lastSaveAttemptRef.current.getTime();
        if (timeSinceLastSave < minTimeBetweenSaves) {
          console.log('Skipping save - too soon since last save attempt and responses unchanged:', timeSinceLastSave, 'ms');
          return;
        }
      }
      
      lastSaveAttemptRef.current = now;
      // DON'T set lastSavedResponsesRef here - only set it after successful save
      
      // Check what type of responses we have
      const responseTypes = responses.map((r, idx) => {
        if (r.option_id !== undefined) return `Q${idx}: multiple_choice (single)`;
        if (r.selected_option_ids && r.selected_option_ids.length > 0) return `Q${idx}: multiple_choice (multiple)`;
        if (r.text_answer?.trim()) return `Q${idx}: text (${r.text_answer.length} chars)`;
        if (r.audio_file) return `Q${idx}: audio`;
        return null;
      }).filter(Boolean);
      
      console.log('Auto-saving to server:', { 
        responsesCount: responses.length, 
        currentQuestionIndex,
        hasUnsavedChanges,
        responseTypes,
        debounceTime,
        hasActualResponses: responses.some(r => r.option_id || r.selected_option_ids || r.text_answer || r.audio_file)
      });
      saveToServer();
    }, debounceTime);

    return () => clearTimeout(autoSaveTimer);
  }, [responses, currentQuestionIndex, userName, userEmail, hasUnsavedChanges, quiz, submitted, startTime, saveToServer, saving]);

  // Session keep-alive is now handled globally in AuthContext
  // No need for quiz-specific keep-alive

  // Mark as having unsaved changes
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  // Timer functions
  const initializeTimer = useCallback(() => {
    if (!quiz?.time_limit_minutes) return;
    
    const totalSeconds = quiz.time_limit_minutes * 60;
    const remainingSeconds = totalSeconds - elapsedSeconds;
    
    console.log('Initializing timer:', { 
      totalSeconds, 
      elapsedSeconds, 
      remainingSeconds,
      isRestored: elapsedSeconds > 0
    });
    
    if (remainingSeconds <= 0) {
      console.log('Timer already expired');
      setTimeRemaining(0);
      setTimerExpired(true);
      return;
    }
    
    console.log('Setting time remaining to:', remainingSeconds, 'seconds');
    setTimeRemaining(remainingSeconds);
  }, [quiz?.time_limit_minutes, elapsedSeconds]);

  // Server-side time validation
  const validateTimeWithServer = useCallback(async () => {
    if (!quiz?.time_limit_minutes || !startTime || !id) return true;
    
    try {
      const response = await api.post('/api/quizzes/validate-session', {
        quiz_id: parseInt(id),
        started_at: startTime.toISOString()
      });
      
      if (!response.data.valid) {
        setTimerExpired(true);
        setTimeRemaining(0);
        return false;
      }
      
      // Update local timer with server time
      const serverRemainingMinutes = response.data.remainingTime;
      setTimeRemaining(Math.floor(serverRemainingMinutes * 60));
      return true;
    } catch (error) {
      console.error('Error validating session with server:', error);
      // On error, assume session is still valid but log the issue
      return true;
    }
  }, [quiz?.time_limit_minutes, startTime, id]);

  // Play audio alert for time warnings
  const playTimeAlert = useCallback((type: 'warning' | 'critical' | 'final') => {
    try {
      // Create audio context for beep sounds
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for different warning levels
      if (type === 'warning') {
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Medium pitch
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      } else if (type === 'critical') {
        // Two beeps for critical
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime); // Higher pitch
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.15);
        
        // Second beep
        setTimeout(() => {
          const oscillator2 = audioContext.createOscillator();
          const gainNode2 = audioContext.createGain();
          oscillator2.connect(gainNode2);
          gainNode2.connect(audioContext.destination);
          oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime);
          gainNode2.gain.setValueAtTime(0.4, audioContext.currentTime);
          oscillator2.start();
          oscillator2.stop(audioContext.currentTime + 0.15);
        }, 200);
      } else if (type === 'final') {
        // Rapid beeps for final warning
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            const oscillator3 = audioContext.createOscillator();
            const gainNode3 = audioContext.createGain();
            oscillator3.connect(gainNode3);
            gainNode3.connect(audioContext.destination);
            oscillator3.frequency.setValueAtTime(1200, audioContext.currentTime); // Highest pitch
            gainNode3.gain.setValueAtTime(0.5, audioContext.currentTime);
            oscillator3.start();
            oscillator3.stop(audioContext.currentTime + 0.1);
          }, i * 150);
        }
      }
    } catch (error) {
      console.log('Audio alert not supported or failed:', error);
    }
  }, []);

  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Timer effect
  useEffect(() => {
    if (!quiz?.time_limit_minutes || timerExpired || submitted) return;
    
    initializeTimer();
    
    const interval = setInterval(() => {
      // Increment elapsed seconds (both state and ref)
      setElapsedSeconds(prev => {
        const newValue = prev + 1;
        elapsedSecondsRef.current = newValue; // Keep ref in sync
        return newValue;
      });
      
      setTimeRemaining(prev => {
        if (prev === null || prev <= 0) {
          setTimerExpired(true);
          return 0;
        }
        
        // Show warning when 5 minutes remaining (only once)
        if (prev === 300) {
          setShowTimeWarning(true);
          playTimeAlert('warning');
        }
        
        // Show warning when 1 minute remaining (override previous warning)
        if (prev === 60) {
          setShowTimeWarning(true);
          playTimeAlert('critical');
        }
        
        // Show final warning at 30 seconds
        if (prev === 30) {
          setShowTimeWarning(true);
          playTimeAlert('final');
        }
        
        // Additional audio alerts without modal
        if (prev === 10) {
          playTimeAlert('final');
        }
        
        return prev - 1;
      });
    }, 1000);
    
    // Validate with server every 30 seconds
    const serverValidationInterval = setInterval(() => {
      validateTimeWithServer();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearInterval(serverValidationInterval);
    };
  }, [quiz?.time_limit_minutes, timerExpired, submitted, initializeTimer, validateTimeWithServer, playTimeAlert]);

  // Auto-submit when timer expires
  useEffect(() => {
    // Only run this effect when timer has actually expired
    if (!timerExpired) {
      console.log('Auto-submit effect: Timer not expired, skipping');
      return;
    }

    console.log('Auto-submit effect triggered:', { 
      timerExpired, 
      submitted, 
      submitting, 
      startTime: startTime?.toISOString(), 
      userName: userName.trim(), 
      userEmail: userEmail.trim(),
      requireEmail: quiz?.require_email
    });

    const autoSubmitQuiz = async () => {
      if (!quiz || !userName.trim() || (quiz?.require_email && !userEmail.trim()) || !startTime) {
        console.log('Timer expired but user details missing - cannot auto-submit');
        console.log('Missing data:', { 
          quiz: !!quiz, 
          userName: userName.trim(), 
          userEmail: userEmail.trim(), 
          requireEmail: quiz?.require_email,
          startTime: !!startTime 
        });
        return;
      }

      try {
        console.log('Auto-submitting quiz due to timer expiration...');
        console.log('=== AUTO-SUBMIT DEBUG ===');
        console.log('Quiz:', quiz);
        console.log('User Name:', userName);
        console.log('User Email:', userEmail);
        console.log('Start Time:', startTime);
        console.log('Responses:', responses);
        
        setSubmitting(true);
        
        // Create the response
        const responseData = {
          participant_name: userName,
          participant_email: quiz?.require_email ? userEmail : '',
          quiz_id: quiz.id,
          session_id: sessionId, // Include session ID to mark server progress as completed
          started_at: startTime.toISOString(),
          completed_at: new Date().toISOString(),
          answers: responses.map(resp => ({
            question_id: resp.question_id,
            option_id: resp.option_id,
            selected_option_ids: resp.selected_option_ids, // Add this for multiple selections
            text_answer: resp.text_answer,
            audio_answer: resp.audio_file
          }))
        };
        
        console.log('=== AUTO-SUBMIT SENDING TO BACKEND ===');
        console.log('Response Data:', JSON.stringify(responseData, null, 2));
        
        const response = await api.post(`/api/quizzes/${quiz.id}/responses`, responseData);
        
        console.log('=== AUTO-SUBMIT BACKEND RESPONSE ===');
        console.log('Backend response:', response.data);
        
        // Clear saved progress on successful submission
        clearSavedProgress();
        clearAudioPlayCounts();
        
        setSubmitting(false);
        setSubmitted(true);
        console.log('Auto-submit completed successfully');
        
      } catch (err: any) {
        console.error('Error submitting quiz:', err);
        console.error('Auto-submit error details:', err.response?.data);
        alert('Failed to submit quiz. Please try again.');
        setSubmitting(false);
      }
    };

    if (!submitted && !submitting && startTime && userName.trim() && (!quiz?.require_email || userEmail.trim())) {
      console.log('Timer expired, auto-submitting quiz...');
      autoSubmitQuiz();
    } else if (!submitted && !submitting && (!userName.trim() || (quiz?.require_email && !userEmail.trim()))) {
      // If user hasn't entered their details, we can't submit
      console.log('Timer expired but user details missing - cannot auto-submit');
    }
  }, [timerExpired, submitted, submitting, quiz, responses, clearSavedProgress, clearAudioPlayCounts, startTime, userName, userEmail]);

  // Save progress and warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!submitted && startTime && userName.trim() && id && hasUnsavedChanges) {
        // Try to save to server using sendBeacon (works even during page unload)
        // This is the last chance to save to server before user navigates away
        try {
          const progressData = {
            participant_name: userName,
            participant_email: userEmail || '',
            session_id: sessionId,
            started_at: startTime.toISOString(),
            current_question_index: currentQuestionIndex,
            elapsed_seconds: elapsedSecondsRef.current, // Use ref to avoid dependency on timer ticks
            responses: responses
          };
          
          // sendBeacon can send data even during page unload
          // Note: This uses cookies for auth (withCredentials), not custom headers
          const blob = new Blob([JSON.stringify(progressData)], { 
            type: 'application/json' 
          });
          
          // Try to send via sendBeacon (works with cookies/auth)
          const url = `${API_URL}/api/quizzes/${id}/progress`;
          const success = navigator.sendBeacon(url, blob);
          
          if (success) {
            console.log('✅ Progress sent to server via sendBeacon on page unload');
          } else {
            console.warn('⚠️ sendBeacon failed - progress may not be saved to server');
          }
        } catch (error) {
          console.error('❌ Failed to send progress via sendBeacon on unload:', error);
        }
        
        // Warn user if there are unsaved changes
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, submitted, startTime, userName, userEmail, id, responses, currentQuestionIndex, sessionId]); // Removed elapsedSeconds to prevent timer spam

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the grouped endpoint to get better organization
        const response = await api.get(`/api/quizzes/${id}/grouped`);
        console.log('Loaded quiz data:', response.data); // Debug log
        setQuiz(response.data);
        
        // Initialize the responses array with empty responses for each question
        if (response.data.questions) {
          const initialResponses = response.data.questions.map((question: Question) => ({
            question_id: question.id
          }));
          setResponses(initialResponses);
          console.log('Initialized responses:', initialResponses); // Debug log
          console.log('Questions from API:', response.data.questions.map((q: Question) => ({ id: q.id, text: q.text })));
        }
        
        // Check for saved progress after quiz is loaded
        // Try server first, then fall back to localStorage
        let savedProgress = await loadFromServer();
        if (!savedProgress) {
          savedProgress = loadFromLocalStorage();
        }
        
        if (savedProgress && !submitted) {
          console.log('Found saved progress, showing recovery prompt');
          setShowRecoveryPrompt(true);
        }
        
        setLoading(false);
      } catch (err: any) {
        // Show a friendly message for unauthorized/forbidden
        const status = err?.response?.status;
        setErrorCode(typeof status === 'number' ? status : null);
        if (status === 401) {
          setError('You need a valid guest link or to log in to access this quiz.');
        } else if (status === 403) {
          setError('Access denied. Your guest link does not allow access to this quiz.');
        } else {
          setError(err.message || 'Failed to fetch quiz');
        }
        setLoading(false);
      }
    };
    
    if (id) {
      fetchQuiz();
    }
  }, [id, loadFromLocalStorage, submitted]);

  // Recovery prompt handlers
  const handleRestoreProgress = async () => {
    // Try to load from server first, then fall back to localStorage
    let savedProgress = await loadFromServer();
    if (!savedProgress) {
      savedProgress = loadFromLocalStorage();
    }
    
    if (savedProgress) {
      setResponses(savedProgress.responses);
      setCurrentQuestionIndex(savedProgress.current_question_index || savedProgress.currentQuestionIndex || 0);
      setUserName(savedProgress.participant_name || savedProgress.userName);
      setUserEmail(savedProgress.participant_email || savedProgress.userEmail || '');
      setStartTime(new Date(savedProgress.started_at || savedProgress.startTime));
      setLastSaved(new Date(savedProgress.last_saved_at || savedProgress.lastSaved));
      const restoredElapsed = savedProgress.elapsed_seconds || savedProgress.elapsedSeconds || 0;
      setElapsedSeconds(restoredElapsed); // Restore elapsed time
      elapsedSecondsRef.current = restoredElapsed; // Keep ref in sync
      setShowUserDetailsForm(false); // Skip user details form if restoring
      // Clear error state when restoring progress
      setServerSaveFailed(false);
      setServerSaveError(null);
      console.log('Restored progress:', savedProgress); // Debug log
    }
    setShowRecoveryPrompt(false);
  };

  const handleStartFresh = () => {
    clearSavedProgress();
    setShowRecoveryPrompt(false);
    setShowUserDetailsForm(true); // Show user details form for fresh start
    
    // Reset all states to initial values
    setUserName('');
    setUserEmail('');
    setStartTime(null);
    setCurrentQuestionIndex(0);
    setSubmitted(false);
    setSubmitting(false);
    setShowReview(false);
    setElapsedSeconds(0); // Reset elapsed time
    elapsedSecondsRef.current = 0; // Keep ref in sync
    setTimeRemaining(null);
    setTimerExpired(false);
    // Clear error state when starting fresh
    setServerSaveFailed(false);
    setServerSaveError(null);
    
    // Reinitialize responses
    if (quiz?.questions) {
      const initialResponses = quiz.questions.map((question: Question) => ({
        question_id: question.id
      }));
      setResponses(initialResponses);
    }
    
    console.log('Started fresh - cleared all data'); // Debug log
  };

  const currentQuestion = quiz?.questions?.[currentQuestionIndex];
  
  // Circular timer drawing constants (SVG-based for crisp edges)
  const TIMER_SIZE = 175; // px
  const TIMER_STROKE = 7; // px
  const timerRadius = (TIMER_SIZE - TIMER_STROKE) / 2;
  const timerCircumference = 2 * Math.PI * timerRadius;
  
  const handleMultipleChoiceAnswer = (optionId: number) => {
    console.log('=== MULTIPLE CHOICE ANSWER ===');
    console.log('Question index:', currentQuestionIndex);
    console.log('Option ID:', optionId);
    console.log('Allow multiple answers:', currentQuestion?.allow_multiple_answers);
    console.log('Current responses before update:', responses);
    
    setResponses(prev => {
      const updated = [...prev];
      
      // Ensure the array has enough elements
      while (updated.length <= currentQuestionIndex) {
        const questionId = quiz?.questions?.[updated.length]?.id;
        updated.push({ question_id: questionId || 0 });
      }
      
      const currentResponse = updated[currentQuestionIndex];
      const questionId = currentQuestion?.id || currentResponse?.question_id || 0;
      
      if (currentQuestion?.allow_multiple_answers) {
        // Multiple selection mode
        const currentSelected = currentResponse?.selected_option_ids || [];
        const isCurrentlySelected = currentSelected.includes(optionId);
        
        const newSelected = isCurrentlySelected
          ? currentSelected.filter(id => id !== optionId) // Deselect
          : [...currentSelected, optionId]; // Select
        
        updated[currentQuestionIndex] = {
          ...currentResponse,
          question_id: questionId,
          selected_option_ids: newSelected,
          // Clear single selection field
          option_id: undefined
        };
      } else {
        // Single selection mode (existing behavior)
        updated[currentQuestionIndex] = {
          ...currentResponse,
          question_id: questionId,
          option_id: optionId,
          // Clear multiple selection field
          selected_option_ids: undefined
        };
      }
      
      console.log('Updated responses:', updated);
      return updated;
    });
    markUnsaved();
  };
  
  const handleTextAnswer = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textAnswer = e.target.value;
    console.log('=== TEXT ANSWER ===');
    console.log('Question index:', currentQuestionIndex);
    console.log('Text answer:', textAnswer);
    console.log('Current responses before update:', responses);
    
    setResponses(prev => {
      const updated = [...prev];
      
      // Ensure the array has enough elements
      while (updated.length <= currentQuestionIndex) {
        const questionId = quiz?.questions?.[updated.length]?.id;
        updated.push({ question_id: questionId || 0 });
      }
      
      // Update the response at the current index
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        question_id: currentQuestion?.id || updated[currentQuestionIndex]?.question_id || 0,
        text_answer: textAnswer
      };
      
      console.log('Updated responses:', updated);
      return updated;
    });
    markUnsaved();
  };
  
  const handleAudioRecorded = async (audioBlob: Blob) => {
    try {
      // Upload the audio recording
      const formData = new FormData();
      formData.append('audio', audioBlob);
      
      const response = await api.post('/api/audio/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const audioFile = response.data.audioFile;
      
      // Save the response
      setResponses(prev => {
        const updated = [...prev];
        
        // Ensure the array has enough elements
        while (updated.length <= currentQuestionIndex) {
          const questionId = quiz?.questions?.[updated.length]?.id;
          updated.push({ question_id: questionId || 0 });
        }
        
        // Update the response at the current index
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          question_id: currentQuestion?.id || updated[currentQuestionIndex]?.question_id || 0,
          audio_answer: audioBlob,
          audio_file: audioFile
        };
        
        return updated;
      });
      markUnsaved();
    } catch (err) {
      console.error('Error uploading audio recording:', err);
      alert('Failed to upload audio recording. Please try again.');
    }
  };
  
  const goToNextQuestion = () => {
    if (currentQuestionIndex < (quiz?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      markUnsaved();
    }
  };
  
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      markUnsaved();
    }
  };

  const goToQuestion = (index: number) => {
    if (index >= 0 && index < (quiz?.questions?.length || 0)) {
      setCurrentQuestionIndex(index);
      setShowReview(false);
      markUnsaved();
    }
  };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(e.target.value);
    markUnsaved();
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value);
    markUnsaved();
  };

  const handleStartQuiz = () => {
    console.log('Starting quiz with:', { userName, userEmail, requireEmail: quiz?.require_email }); // Debug log
    
    if (!userName.trim()) {
      alert('Please enter your name to start the quiz');
      return;
    }
    if (quiz?.require_email && !userEmail.trim()) {
      alert('Please enter your email to start the quiz');
      return;
    }
    
    // Check if quiz has questions
    if (!quiz?.questions || quiz.questions.length === 0) {
      alert('This quiz has no questions. Please contact the administrator.');
      console.error('Quiz has no questions:', quiz);
      return;
    }
    
    console.log('Quiz validation passed, starting quiz...'); // Debug log
    console.log('Setting showUserDetailsForm to false and startTime to now');
    console.log('Quiz has', quiz.questions.length, 'questions');
    
    // Reset timer state for fresh start
    setTimerExpired(false);
    setTimeRemaining(null);
    setElapsedSeconds(0); // Start with 0 elapsed time
    
    setShowUserDetailsForm(false);
    setStartTime(new Date());
    console.log('Quiz started - should now show questions');
  };
  
  const isCurrentQuestionAnswered = () => {
    const response = responses[currentQuestionIndex];
    if (!response) return false;
    
    if (currentQuestion?.question_type === 'multiple_choice') {
      return response.option_id !== undefined;
    } else if (currentQuestion?.question_type === 'text') {
      return !!response.text_answer?.trim();
    } else if (currentQuestion?.question_type === 'audio_response') {
      return !!response.audio_file;
    }
    
    return false;
  };

  const isQuestionAnswered = (index: number) => {
    const response = responses[index];
    const question = quiz?.questions?.[index];
    if (!response || !question) return false;
    
    if (question.question_type === 'multiple_choice') {
      if (question.allow_multiple_answers) {
        return response.selected_option_ids !== undefined && response.selected_option_ids.length > 0;
      } else {
        return response.option_id !== undefined;
      }
    } else if (question.question_type === 'text') {
      return !!response.text_answer?.trim();
    } else if (question.question_type === 'audio_response') {
      return !!response.audio_file;
    }
    
    return false;
  };
  
  const areAllQuestionsAnswered = () => {
    if (!quiz?.questions) return false;
    
    return quiz.questions.every((question, index) => {
      const response = responses[index];
      if (!response) return false;
      
      if (question.question_type === 'multiple_choice') {
        if (question.allow_multiple_answers) {
          return response.selected_option_ids !== undefined && response.selected_option_ids.length > 0;
        } else {
          return response.option_id !== undefined;
        }
      } else if (question.question_type === 'text') {
        return !!response.text_answer?.trim();
      } else if (question.question_type === 'audio_response') {
        return !!response.audio_file;
      }
      
      return false;
    });
  };

  const getAnsweredCount = useCallback(() => {
    if (!quiz?.questions) return 0;
    
    console.log('=== getAnsweredCount DEBUG ===');
    console.log('Responses array length:', responses.length);
    console.log('Questions array length:', quiz.questions.length);
    console.log('Responses:', responses);
    
    // Count answered questions, considering that responses array might be shorter than questions array
    let count = 0;
    for (let i = 0; i < quiz.questions.length; i++) {
      const response = responses[i];
      const question = quiz.questions[i];
      
      if (!response || !question) continue;
      
      if (question.question_type === 'multiple_choice') {
        if (question.allow_multiple_answers && response.selected_option_ids && response.selected_option_ids.length > 0) {
          count++;
          console.log(`Question ${i} (multiple_choice) answered with options ${response.selected_option_ids}`);
        } else if (!question.allow_multiple_answers && response.option_id !== undefined) {
          count++;
          console.log(`Question ${i} (multiple_choice) answered with option ${response.option_id}`);
        }
      } else if (question.question_type === 'text' && response.text_answer?.trim()) {
        count++;
        console.log(`Question ${i} (text) answered with: "${response.text_answer}"`);
      } else if (question.question_type === 'audio_response' && response.audio_file) {
        count++;
        console.log(`Question ${i} (audio) answered`);
      }
    }
    
    console.log('Total answered:', count);
    return count;
  }, [quiz?.questions, responses]);

  const getSelectedOptionText = (questionIndex: number) => {
    const response = responses[questionIndex];
    const question = quiz?.questions?.[questionIndex];
    
    if (question?.allow_multiple_answers && response?.selected_option_ids && response.selected_option_ids.length > 0 && question?.options) {
      const selectedOptions = question.options.filter(opt => response.selected_option_ids?.includes(opt.id));
      return selectedOptions.map(opt => opt.text).join(', ');
    } else if (response?.option_id && question?.options) {
      const selectedOption = question.options.find(opt => opt.id === response.option_id);
      return selectedOption?.text || '';
    }
    return '';
  };
  
    const handleSubmit = useCallback(async () => {
    console.log('=== SUBMIT QUIZ DEBUG ===');
    console.log('Quiz:', quiz);
    console.log('User Name:', userName);
    console.log('User Email:', userEmail);
    console.log('Start Time:', startTime);
    console.log('Responses:', responses);
    console.log('Require Email:', quiz?.require_email);
    
    if (!quiz || !userName.trim() || (quiz?.require_email && !userEmail.trim()) || !startTime) {
      const missingFields = [];
      if (!userName.trim()) missingFields.push('name');
      if (quiz?.require_email && !userEmail.trim()) missingFields.push('email');
      console.error('Missing required fields:', missingFields);
      alert(`Please enter your ${missingFields.join(' and ')} before submitting`);
      return;
    }

    try {
      setSubmitting(true);
      
      // Create the response, ensuring we have answers for all questions
      const allAnswers = [];
      for (let i = 0; i < quiz.questions.length; i++) {
        const response = responses[i];
        const question = quiz.questions[i];
        
        if (response) {
          allAnswers.push({
            question_id: response.question_id || question.id,
            option_id: response.option_id,
            selected_option_ids: response.selected_option_ids, // Add this for multiple selections
            text_answer: response.text_answer,
            audio_answer: response.audio_file
          });
        } else {
          // Add empty answer for unanswered question
          allAnswers.push({
            question_id: question.id,
            option_id: undefined,
            selected_option_ids: undefined,
            text_answer: undefined,
            audio_answer: undefined
          });
        }
      }
      
      const responseData = {
        participant_name: userName,
        participant_email: quiz?.require_email ? userEmail : '',
        quiz_id: quiz.id,
        session_id: sessionId, // Include session ID to mark server progress as completed
        started_at: startTime.toISOString(),
        completed_at: new Date().toISOString(),
        answers: allAnswers
      };
      
      console.log('=== SENDING TO BACKEND ===');
      console.log('Response Data:', JSON.stringify(responseData, null, 2));
      
      const response = await api.post(`/api/quizzes/${quiz.id}/responses`, responseData);
      
      console.log('=== BACKEND RESPONSE ===');
      console.log('Backend response:', response.data);
      
      // Store the answered count before clearing anything
      const answeredCount = getAnsweredCount();
      setSubmittedAnswerCount(answeredCount);
      
      // Clear saved progress on successful submission
      clearSavedProgress();
      clearAudioPlayCounts();
      
      setSubmitting(false);
      setSubmitted(true);
      
    } catch (err: any) {
      console.error('Error submitting quiz:', err);
      console.error('Error details:', err.response?.data);
      alert('Failed to submit quiz. Please try again.');
      setSubmitting(false);
    }
  }, [quiz, userName, userEmail, startTime, responses, clearSavedProgress, clearAudioPlayCounts, getAnsweredCount]);

  // Keyboard navigation for evaluations: Enter key to go to next question, review, or submit
  useEffect(() => {
    // Only enable for evaluations
    if (quiz?.quiz_type !== 'evaluation') return;
    
    // Don't enable if we're in user details form or submitted
    if (showUserDetailsForm || submitted || submitting) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in a regular input field (but allow textarea)
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        return;
      }
      
      // Only handle Enter key (without Shift - Shift+Enter creates new line in textarea)
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Prevent default to stop new line in textarea
        e.preventDefault();
        
        // If in review mode, submit the evaluation
        if (showReview) {
          // Only submit if the submit button would not be disabled
          if (userName.trim() && (!quiz?.require_email || userEmail.trim())) {
            handleSubmit();
          }
          return;
        }
        
        // If on the last question, go to review
        if (currentQuestionIndex >= (quiz?.questions?.length || 0) - 1) {
          setShowReview(true);
          return;
        }
        
        // Otherwise, go to next question
        if (currentQuestionIndex < (quiz?.questions?.length || 0) - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          markUnsaved();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [quiz?.quiz_type, quiz?.questions?.length, quiz?.require_email, showUserDetailsForm, showReview, submitted, submitting, currentQuestionIndex, userName, userEmail, markUnsaved, handleSubmit]);

  // Auto-save status component
  const AutoSaveStatus = () => {
    if (saving) {
      return (
        <div className="auto-save-status saving">
          <div className="loading-spinner"></div>
          <span className="auto-save-label">Saving...</span>
        </div>
      );
    }
    
    // Show warning if server save failed
    if (serverSaveFailed && serverSaveError) {
      return (
        <div className="auto-save-status error" style={{ 
          color: '#c62828', 
          backgroundColor: '#ffebee',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #f44336',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span className="auto-save-label" style={{ fontWeight: 'bold' }}>
              Server Save Failed
            </span>
            <span style={{ fontSize: '12px', opacity: 0.9 }}>
              {serverSaveError}
            </span>
          </div>
        </div>
      );
    }
    
    if (lastSaved) {
      const formattedTime = lastSaved.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      return (
        <div className="auto-save-status saved">
          <span className="auto-save-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="m15.14 8.19-5.002 5.287-2.717-2.57" stroke="#345997"/>
              <path d="M11 19.25a8.25 8.25 0 1 0 0-16.5 8.25 8.25 0 0 0 0 16.5z" stroke="#345997"/>
            </svg>
          </span>
          <span className="auto-save-label">Saved {formattedTime}</span>
        </div>
      );
    }
    
    if (hasUnsavedChanges) {
      return (
        <div className="auto-save-status unsaved">
          ● Unsaved changes
        </div>
      );
    }
    
    return null;
  };


  
  if (loading) {
    console.log('Rendering: Loading state');
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading quiz...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !quiz) {
    console.log('Rendering: Error state', { error, quiz: !!quiz });
    return (
      <div className="page">
        <div className="container">
          <div className="card" style={{ backgroundColor: '#ffebee', color: '#c62828' }}>
            <p>{error || 'Quiz not found'}</p>
            <button 
              className="btn"
              onClick={() => navigate('/')}
              style={{ marginTop: '15px' }}
            >
              Go Back Home
            </button>
            {errorCode === 401 && (
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/login')}
                style={{ marginTop: '15px', marginLeft: '10px' }}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Recovery prompt modal
  if (showRecoveryPrompt && !loading) {
    console.log('Rendering: Recovery prompt');
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <div className="recovery-prompt">
              <div className="recovery-icon">🔄</div>
              <h2>Continue Where You Left Off?</h2>
              <p>We found saved progress for this quiz from your previous session.</p>
              
              <div className="recovery-details">
                <p><strong>Quiz:</strong> {quiz.title}</p>
                {(() => {
                  // Show saved data from server or localStorage
                  const checkSavedData = async () => {
                    let savedData = await loadFromServer();
                    if (!savedData) {
                      savedData = loadFromLocalStorage();
                    }
                    return savedData;
                  };
                  
                  // Use a simple synchronous check for display
                  const savedData = loadFromLocalStorage();
                  if (savedData) {
                    const savedTimeStr: string = (savedData.lastSaved || savedData.last_saved_at) ?? new Date().toISOString();
                    const savedTime = new Date(savedTimeStr);
                    const savedIndex = savedData.currentQuestionIndex || savedData.current_question_index || 0;
                    const userName = savedData.userName || savedData.participant_name;
                    const userEmail = savedData.userEmail || savedData.participant_email;
                    return (
                      <>
                        <p><strong>Last saved:</strong> {savedTime.toLocaleString()}</p>
                        <p><strong>Progress:</strong> Question {savedIndex + 1} of {quiz.questions.length}</p>
                        <p><strong>Name:</strong> {userName}</p>
                        {userEmail && <p><strong>Email:</strong> {userEmail}</p>}
                      </>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <div className="recovery-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleRestoreProgress}
                >
                  Restore Progress
                </button>
                <button 
                  className="btn btn-outline"
                  onClick={handleStartFresh}
                >
                  Start Fresh
                </button>
              </div>
              
              <p className="recovery-note">
                Note: Starting fresh will delete your saved progress.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (submitted) {
    console.log('Rendering: Submitted state');
    return (
      <div className="page">
        <div className="container">
          <div className="card success-card">
                            {/* <div className="success-icon">✓</div> */}
            <h1 className="success-title">Thank You!</h1>
            <p className="success-message">
              {quiz.quiz_type === 'evaluation' 
                ? 'Your evaluation has been submitted successfully.' 
                : 'Your quiz has been submitted successfully.'
              }
            </p>
            <div className="success-details">
              <p><strong>{quiz.quiz_type === 'evaluation' ? 'Evaluation:' : 'Quiz:'}</strong> {quiz.title}</p>
              <p><strong>Submitted at:</strong> {new Date().toLocaleString()}</p>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => navigate('/')}
              style={{ marginTop: '20px' }}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Review mode
  if (showReview) {
    console.log('Rendering: Review mode');
    return (
      <div className="page">
        <div className="container">
          <div className="card review-intro-card">
            <div className="review-header">
              <h1 className="quiz-title">{quiz.title}</h1>
              
              <div className="review-progress">
                <div className="progress-summary">
                  <span className="progress-count">
                    {getAnsweredCount()} of {quiz.questions.length} answered
                  </span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${(getAnsweredCount() / quiz.questions.length) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card review-card">
            <div className="review-section-header">
              <h2>Review Your Answers</h2>
              <p>Review your responses below. Click on any question to edit your answer.</p>
            </div>

            <div className="review-questions">
              {quiz.questions.map((question, index) => (
                <div 
                  key={question.id} 
                  className={`review-question-item ${isQuestionAnswered(index) ? 'answered' : 'unanswered'}`}
                >
                  <div className="review-question-header">
                    <div className="question-info">
                      <span className="question-number">Question {index + 1}</span>
                      <span className={`question-status ${isQuestionAnswered(index) ? 'answered' : 'unanswered'}`}>
                        {isQuestionAnswered(index) ? 'Answered' : 'Not answered'}
                      </span>
                    </div>
                    <button 
                      className="btn btn-small btn-outline edit-question-btn"
                      onClick={() => goToQuestion(index)}
                    >
                      {isQuestionAnswered(index) ? 'Edit' : 'Answer'}
                    </button>
                  </div>
                  
                  <div className="review-question-content">
                    <h3 className="question-text">{question.text}</h3>
                    
                    {question.audio_file && (
                      <div className="question-audio">
                        <AudioPlayer 
                          src={`${API_URL}/uploads/${question.audio_file}`} 
                          title="Question audio" 
                          {...audioRestrictionProps(question.audio_file)}
                        />
                      </div>
                    )}
                    
                    <div className="review-answer">
                      {question.question_type === 'multiple_choice' && (
                        <div className="answer-preview">
                          <strong>Your answer:</strong>
                          {isQuestionAnswered(index) ? (
                            <span className="selected-answer">{getSelectedOptionText(index)}</span>
                          ) : (
                            <span className="no-answer">No answer selected</span>
                          )}
                        </div>
                      )}
                      
                      {question.question_type === 'text' && (
                        <div className="answer-preview">
                          <strong>Your answer:</strong>
                          {responses[index]?.text_answer ? (
                            <div className="text-answer-preview">
                              {responses[index].text_answer}
                            </div>
                          ) : (
                            <span className="no-answer">No answer provided</span>
                          )}
                        </div>
                      )}
                      
                      {question.question_type === 'audio_response' && (
                        <div className="answer-preview">
                          <strong>Your answer:</strong>
                          {responses[index]?.audio_file ? (
                            <div className="audio-answer-preview">
                              <AudioPlayer 
                                src={`${API_URL}/uploads/${responses[index].audio_file}`} 
                                title="Your recorded answer"
                              />
                            </div>
                          ) : (
                            <span className="no-answer">No audio recorded</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="review-actions">
              <button
                className="btn btn-outline"
                onClick={() => setShowReview(false)}
              >
                ← Continue Editing
              </button>
              
              {!areAllQuestionsAnswered() && (
                <div className="incomplete-warning">
                  <p>You haven't answered all questions yet. You can still submit, but consider completing all questions for the best results.</p>
                </div>
              )}
            </div>
          </div>

          <div className="card submit-card">
            <h2>Your Information</h2>
            
            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={userName}
                onChange={handleNameChange}
                placeholder="Enter your full name"
                required
              />
            </div>
            
            {quiz?.require_email && (
              <div className="form-group">
                <label htmlFor="email">Your Email *</label>
                <input
                  type="email"
                  id="email"
                  className="form-control"
                  value={userEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            )}
            
            <div className="submit-summary">
              <h3>Submission Summary</h3>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Questions answered:</span>
                  <span className="stat-value">{getAnsweredCount()} of {quiz.questions.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Completion:</span>
                  <span className="stat-value">{Math.round((getAnsweredCount() / quiz.questions.length) * 100)}%</span>
                </div>
              </div>
            </div>
            
            <button
              className="btn btn-primary btn-large submit-btn"
              onClick={handleSubmit}
              disabled={submitting || !userName.trim() || (quiz?.require_email && !userEmail.trim())}
            >
              {submitting ? (
                <>
                  <div className="loading-spinner"></div>
                  Submitting...
                </>
              ) : (
                <>
                  {quiz.quiz_type === 'evaluation' ? 'Submit Evaluation' : 'Submit Quiz'}
                </>
              )}
            </button>
            
            {(!userName.trim() || (quiz?.require_email && !userEmail.trim())) && (
              <p className="form-error">Please enter your {!userName.trim() ? 'name' : ''}{!userName.trim() && quiz?.require_email && !userEmail.trim() ? ' and ' : ''}{quiz?.require_email && !userEmail.trim() ? 'email' : ''} to submit</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // User details form (shown first)
  if (showUserDetailsForm) {
    console.log('Rendering: User details form');
    return (
      <div className="page take-quiz-page">
        <div className="container">
          {/* Quiz Information Section */}
          <div className="quiz-info-section">
            <h1 className="quiz-title">{quiz.title}</h1>
            <p className="quiz-description">{quiz.description}</p>
            
            <div className="quiz-metrics">
              {quiz.time_limit_minutes && (
                <p className="quiz-metric">Time Limit: {quiz.time_limit_minutes} minutes</p>
              )}
              <p className="quiz-metric">Questions: {quiz.questions.length}</p>
            </div>
          </div>

          {/* Before You Start Section */}
          <div className="before-start-section">
            <h2 className="section-title">Before You Start:</h2>
            <p className="section-instruction">
              {quiz.quiz_type === 'evaluation' 
                ? 'Please enter your details to begin the evaluation:' 
                : 'Please enter your details to begin the quiz:'
              }
            </p>
            
            <div className="form-group">
              <label htmlFor="start-name" className="form-label">
                {quiz.quiz_type === 'evaluation' ? 'Trainer name*' : 'Your full name*'}
              </label>
              <input
                type="text"
                id="start-name"
                className="form-control"
                value={userName}
                onChange={handleNameChange}
                placeholder={quiz.quiz_type === 'evaluation' ? 'Trainer Full Name' : 'Your Full Name Here'}
                required
              />
            </div>
            
            {quiz?.require_email && (
              <div className="form-group">
                <label htmlFor="start-email" className="form-label">
                  {quiz.quiz_type === 'evaluation' ? 'Trainer email*' : 'Your email*'}
                </label>
                <input
                  type="email"
                  id="start-email"
                  className="form-control"
                  value={userEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
            )}
            
            <button
              className={`btn btn-primary start-quiz-btn ${quiz?.quiz_type === 'evaluation' ? 'start-quiz-btn-evaluation' : ''}`}
              onClick={handleStartQuiz}
              disabled={!userName.trim() || (quiz?.require_email && !userEmail.trim())}
            >
              {quiz.quiz_type === 'evaluation' ? 'Start Evaluation' : 'Start Quiz'}
            </button>
            
            {(!userName.trim() || (quiz?.require_email && !userEmail.trim())) && (
              <p className="form-error">
                Please enter your {!userName.trim() ? 'name' : ''}{!userName.trim() && quiz?.require_email && !userEmail.trim() ? ' and ' : ''}{quiz?.require_email && !userEmail.trim() ? 'email' : ''} to start
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Question mode
  console.log('Rendering: Question mode', { 
    currentQuestionIndex, 
    totalQuestions: quiz?.questions?.length,
    currentQuestion: !!currentQuestion,
    startTime: startTime?.toISOString(),
    timerExpired,
    submitted,
    submitting
  });

  // Safety check: if we don't have a current question, show an error
  if (!currentQuestion) {
    console.error('No current question available:', { 
      currentQuestionIndex, 
      questionsLength: quiz?.questions?.length,
      questions: quiz?.questions 
    });
    return (
      <div className="page">
        <div className="container">
          <div className="card" style={{ backgroundColor: '#ffebee', color: '#c62828' }}>
            <h2>Error: No Question Available</h2>
            <p>Unable to load question {currentQuestionIndex + 1}. This quiz may have configuration issues.</p>
            <button 
              className="btn"
              onClick={() => navigate('/')}
              style={{ marginTop: '15px' }}
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {/* Time warning modal */}
        {showTimeWarning && timeRemaining !== null && timeRemaining > 0 && (
          <div className="modal-overlay">
            <div className="modal time-warning-modal">
              <div className="modal-header">
                <h3 className="modal-title">
                  {timeRemaining <= 30 ? 'FINAL WARNING' : 'Time Warning'}
                </h3>
              </div>
              <div className="modal-body">
                <p>
                  {timeRemaining <= 30 
                    ? `Only ${timeRemaining} seconds remaining!` 
                    : timeRemaining <= 60
                    ? `Only ${formatTimeRemaining(timeRemaining)} remaining!`
                    : `${formatTimeRemaining(timeRemaining)} remaining.`
                  }
                </p>
                <p>
                  {timeRemaining <= 30 
                    ? 'Your quiz will be submitted automatically when time runs out!'
                    : timeRemaining <= 60
                    ? 'Please complete your quiz quickly!'
                    : 'Please complete your quiz soon.'
                  }
                </p>
                {(!userName.trim() || (quiz?.require_email && !userEmail.trim())) && (
                  <div style={{ 
                    background: '#ffebee', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    border: '1px solid #f44336',
                    marginTop: '12px'
                  }}>
                    <p style={{ color: '#c62828', fontWeight: 'bold', margin: 0 }}>
                      Please enter your {!userName.trim() ? 'name' : ''}{!userName.trim() && quiz?.require_email && !userEmail.trim() ? ' and ' : ''}{quiz?.require_email && !userEmail.trim() ? 'email' : ''} in the review section to ensure your quiz can be submitted!
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowTimeWarning(false)}
                >
                  Continue Quiz
                </button>
                {(!userName.trim() || (quiz?.require_email && !userEmail.trim())) && (
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowTimeWarning(false);
                      setShowReview(true);
                    }}
                  >
                    Go to Review & Enter Details
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Time expired modal */}
        {timerExpired && (
          <div className="modal-overlay">
            <div className="modal time-expired-modal">
              <div className="modal-header">
                <h3 className="modal-title">Time's Up!</h3>
              </div>
              <div className="modal-body">
                <p>The time limit for this quiz has been reached.</p>
                {userName.trim() && (!quiz?.require_email || userEmail.trim()) ? (
                  <p>Your quiz is being submitted automatically with your current answers.</p>
                ) : (
                  <>
                    <p style={{ color: '#c62828', fontWeight: 'bold' }}>
                      Cannot submit automatically - missing {!userName.trim() ? 'name' : ''}{!userName.trim() && quiz?.require_email && !userEmail.trim() ? ' and ' : ''}{quiz?.require_email && !userEmail.trim() ? 'email' : ''}!
                    </p>
                    <p>Please enter your details to submit your quiz manually.</p>
                  </>
                )}
              </div>
              {(!userName.trim() || (quiz?.require_email && !userEmail.trim())) && (
                <div className="modal-footer">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => {
                      setTimerExpired(false);
                      setShowReview(true);
                    }}
                  >
                    Enter Details & Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quiz Overview Section */}
        <div className="quiz-overview-section">
          <AutoSaveStatus />
          <div className="quiz-overview-content">
            <div className="quiz-title-section">
              <h1 className="quiz-title">{quiz.title}</h1>
            </div>
            
            {/* Circular Timer */}
            {quiz.time_limit_minutes && timeRemaining !== null && (
              <div className="circular-timer">
                {(() => {
                  const total = quiz.time_limit_minutes * 60;
                  // Invert: show full blue at start, shrinking to zero as time elapses
                  const remainingFrac = Math.min(1, Math.max(0, timeRemaining / total));
                  const dashOffset = timerCircumference * (1 - remainingFrac);
                  const center = TIMER_SIZE / 2;
                  return (
                    <div className="timer-svg-wrap" style={{ width: TIMER_SIZE, height: TIMER_SIZE }}>
                      <svg className="timer-svg" width={TIMER_SIZE} height={TIMER_SIZE} viewBox={`0 0 ${TIMER_SIZE} ${TIMER_SIZE}`}>
                        <g transform={`rotate(90 ${center} ${center}) scale(-1 1) translate(-${TIMER_SIZE} 0)`}>
                          <circle
                            className="timer-track"
                            cx={center}
                            cy={center}
                            r={timerRadius}
                            strokeWidth={TIMER_STROKE}
                            fill="none"
                          />
                          <circle
                            className="timer-progress"
                            cx={center}
                            cy={center}
                            r={timerRadius}
                            strokeWidth={TIMER_STROKE}
                            fill="none"
                            strokeDasharray={timerCircumference}
                            strokeDashoffset={dashOffset}
                            strokeLinecap="round"
                          />
                        </g>
                      </svg>
                      <div className="timer-center">
                        <div className="timer-value">{formatTimeRemaining(timeRemaining)}</div>
                        <div className="timer-label">Remaining Time</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* Progress Bar */}
            <div className="quiz-progress-section">
              <div className="progress-header">
                <span className="progress-text">Question {currentQuestionIndex + 1} of {quiz.questions.length}</span>
                <span className="progress-percentage">{Math.round(((currentQuestionIndex + 1) / quiz.questions.length) * 100)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${(getAnsweredCount() / quiz.questions.length) * 100}%` 
                  }}
                />
              </div>
              <div className="progress-footer">
                {getAnsweredCount()} of {quiz.questions.length} answered
              </div>
            </div>
          </div>
        </div>
        
        
        
        {/* Audio Group Information */}
        {currentQuestion && currentQuestion.audio_file && quiz.audioGroups && (
          (() => {
            const audioGroup = quiz.audioGroups.find((group: any) => group.audio_file === currentQuestion.audio_file);
            if (audioGroup && audioGroup.questions.length > 1) {
                             const groupQuestionNumbers = audioGroup.questions.map((q: Question) => 
                 quiz.questions.findIndex((qq: Question) => qq.id === q.id) + 1
               ).sort((a: number, b: number) => a - b);
              
              return (
                <div className="card audio-group-card">
                  <div className="audio-group-header">
                    <div className="audio-group-info">
                      <h3>Audio for Questions {groupQuestionNumbers.join(', ')}</h3>
                      <p>This audio recording is shared by {audioGroup.questions.length} questions. Listen carefully and answer each question based on what you hear.</p>
                    </div>
                  </div>
                  
                  <div className="audio-group-player">
                    <AudioPlayer 
                      src={`${API_URL}/uploads/${currentQuestion.audio_file}`} 
                      title="Shared audio recording" 
                      {...audioRestrictionProps(currentQuestion.audio_file)}
                    />
                  </div>
                  
                  <div className="audio-group-progress">
                    <p>
                      <strong>Progress in this audio group:</strong> 
                      {' '}{audioGroup.questions.filter((q: Question) => {
                        const qIndex = quiz.questions.findIndex((qq: Question) => qq.id === q.id);
                        return isQuestionAnswered(qIndex);
                      }).length} of {audioGroup.questions.length} questions answered
                    </p>
                  </div>
                </div>
              );
            }
            return null;
          })()
        )}

        {currentQuestion && (
          <div className="question-card">
            <div className="question-header">
              <div className="question-number-section">
                <span className="question-number">Question {currentQuestionIndex + 1}</span>
                <div className="question-icon">?</div>
              </div>
            </div>
            
            <h2 className="question-text">{currentQuestion.text}</h2>
            
            {/* Shared image group banner */}
            {currentQuestion.image_file && quiz.imageGroups && (() => {
              const imageGroup = quiz.imageGroups.find((g: any) => g.image_file === currentQuestion.image_file);
              if (imageGroup && imageGroup.questions.length > 1) {
                const groupQuestionNumbers = imageGroup.questions.map((q: Question) =>
                  quiz.questions.findIndex((qq: Question) => qq.id === q.id) + 1
                ).sort((a: number, b: number) => a - b);
                return (
                  <div className="card" style={{ marginTop: 10 }}>
                    <div className="card-header"><h3>Image for Questions {groupQuestionNumbers.join(', ')}</h3></div>
                    <div style={{ padding: 12 }}>
                      <ImageViewer src={`${API_URL}/uploads/${currentQuestion.image_file}`} title="Shared image" />
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Show individual audio only if it's not part of a group */}
            {currentQuestion.audio_file && (!quiz.audioGroups || !quiz.audioGroups.find((group: any) => 
              group.audio_file === currentQuestion.audio_file && group.questions.length > 1
            )) && (
              <div className="question-audio">
                <AudioPlayer 
                  src={`${API_URL}/uploads/${currentQuestion.audio_file}`} 
                  title="Listen to the audio" 
                  {...audioRestrictionProps(currentQuestion.audio_file)}
                />
              </div>
            )}

            {/* Standalone image display if not grouped */}
            {currentQuestion.image_file && (!quiz.imageGroups || !quiz.imageGroups.find((group: any) => 
              group.image_file === currentQuestion.image_file && group.questions.length > 1
            )) && (
              <div style={{ marginTop: 10 }}>
                <ImageViewer src={`${API_URL}/uploads/${currentQuestion.image_file}`} title="Question image" />
              </div>
            )}

            {/* Gallery images display */}
            {currentQuestion.images && currentQuestion.images.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {currentQuestion.images.map((img, index) => (
                    <div key={index} style={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <ImageViewer 
                        src={`${API_URL}/uploads/${img.image_file}`} 
                        title={img.caption || `Question image ${index + 1}`} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="answer-section">
              {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
                <>
                  {currentQuestion.allow_multiple_answers && (
                    <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                      <strong>ℹ️ Multiple Answers:</strong> You can select multiple options. Click an option to select/deselect it.
                    </div>
                  )}
                  <div className="options-list">
                    {currentQuestion.options.map(option => {
                      const response = responses[currentQuestionIndex];
                      const isSelected = currentQuestion.allow_multiple_answers
                        ? response?.selected_option_ids?.includes(option.id) || false
                        : response?.option_id === option.id;
                      
                      return (
                        <div 
                          key={option.id}
                          className={`option-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleMultipleChoiceAnswer(option.id)}
                        >
                          <div className={currentQuestion.allow_multiple_answers ? "option-checkbox" : "option-radio"}>
                            {currentQuestion.allow_multiple_answers 
                              ? (isSelected ? '☑' : '☐')
                              : (isSelected ? '●' : '○')
                            }
                          </div>
                          <span className="option-text">
                            {option.text}
                            {('image_file' in option as any) && (option as any).image_file ? (
                              <span style={{ display: 'block', marginTop: 6 }}>
                                <img src={`${API_URL}/uploads/${(option as any).image_file}`} alt="option" style={{ maxHeight: 140, maxWidth: '100%', borderRadius: 6 }} />
                              </span>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              
              {currentQuestion.question_type === 'text' && (
                <div className="text-answer text-answer-input">
                  <textarea
                    placeholder="Type your answer here..."
                    rows={4}
                    className="form-control"
                    value={responses[currentQuestionIndex]?.text_answer || ''}
                    onChange={handleTextAnswer}
                  />
                  <div className="character-count">
                    {responses[currentQuestionIndex]?.text_answer?.length || 0} characters
                  </div>
                </div>
              )}
              
              {currentQuestion.question_type === 'audio_response' && (
                <div className="audio-answer">
                  <p className="audio-instruction">Record your answer:</p>
                  <AudioRecorder onRecordingComplete={handleAudioRecorded} />
                  
                  {responses[currentQuestionIndex]?.audio_file && (
                    <div className="recorded-preview">
                      <p className="preview-label">Your recorded answer:</p>
                      <AudioPlayer 
                        src={`${API_URL}/uploads/${responses[currentQuestionIndex].audio_file}`} 
                        title="Your answer"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="navigation-buttons">
              <button
                className="btn nav-btn-prev"
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                aria-label="Go to previous question"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style={{flexShrink:0}}>
                  <path d="M15.833 10H4.167m0 0L10 15.833M4.167 10 10 4.167" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="btn-label">Previous</span>
              </button>
              
              <div className="nav-center">
                <button
                  className="btn review-btn"
                  onClick={() => setShowReview(true)}
                >
                  <span className="Text-padding">
                    Review ({getAnsweredCount()}/{quiz.questions.length})
                  </span>
                </button>
              </div>
              
              {currentQuestionIndex < quiz.questions.length - 1 ? (
                <button
                  className="btn nav-btn-next"
                  onClick={goToNextQuestion}
                  aria-label="Go to next question"
                >
                  <span className="btn-label">Next</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M4.167 10h11.666m0 0L10 4.167M15.833 10 10 15.833" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <button
                  className="btn nav-btn-next"
                  onClick={() => setShowReview(true)}
                  aria-label="Review answers and submit"
                >
                  <span className="btn-label">Review & Submit</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M4.167 10h11.666m0 0L10 4.167M15.833 10 10 15.833" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TakeQuizPage; 